// Auth-Kontext für alle Plattformen (Besitzer: api-kern): Server-Konfiguration, /me, Anmelden
// (Passwort, Persona, Supabase), Abmelden, Kontowechsel. Rechte prüft immer der Server.
import type { AuthConfig, Me } from '@bar/contracts/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo } from 'react';
import { clearPersonaOverride, isApiError, setAccessTokenProvider } from '../api/client';
import { coreApi, coreKeys } from '../api/core';
import { eventStream } from '../events';
import {
  onSupabaseAuthChange,
  supabaseAccessToken,
  supabaseSignInWithPassword,
  supabaseSignOut,
} from './supabase';

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous' | 'error';

export interface AuthContextValue {
  status: AuthStatus;
  me: Me | null;
  config: AuthConfig | undefined;
  error: unknown;
  /** E-Mail + Passwort (local/demo über den Server, supabase über Supabase Auth). */
  login(email: string, password: string): Promise<Me | null>;
  /** Nur Modus demo: Ein-Klick-Login je Persona. */
  loginPersona(key: string): Promise<Me>;
  /** Abmelden; `all` beendet alle Konten dieses Browsers. Liefert das verbleibende Konto oder null. */
  logout(options?: { all?: boolean }): Promise<Me | null>;
  /** Zu einem anderen angemeldeten Konto wechseln (local/demo). */
  switchAccount(accountId: string): Promise<Me>;
  /** /me neu laden (z. B. nach Profiländerung). */
  refresh(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** /me oder null bei 401 (anonym); andere Fehler bleiben Fehler. */
async function fetchMeOrNull(): Promise<Me | null> {
  try {
    return await coreApi.me();
  } catch (error) {
    if (isApiError(error) && error.status === 401) return null;
    throw error;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const configQuery = useQuery({
    queryKey: coreKeys.authConfig,
    queryFn: coreApi.authConfig,
    staleTime: Infinity,
  });
  const config = configQuery.data;
  const supabase = config?.mode === 'supabase';

  // Supabase: Token-Lieferant setzen, bevor /me lädt; bei Statuswechsel /me neu laden.
  useEffect(() => {
    if (!config || !supabase) return;
    setAccessTokenProvider(() => supabaseAccessToken(config));
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;
    void onSupabaseAuthChange(
      config,
      () => void queryClient.invalidateQueries({ queryKey: coreKeys.me }),
    ).then((off) => (cancelled ? off() : (unsubscribe = off)));
    return () => {
      cancelled = true;
      unsubscribe?.();
      setAccessTokenProvider(undefined);
    };
  }, [config, supabase, queryClient]);

  const meQuery = useQuery({
    queryKey: coreKeys.me,
    queryFn: fetchMeOrNull,
    enabled: Boolean(config),
    staleTime: 60_000,
    retry: false,
  });

  const setMe = useCallback(
    (me: Me | null) => queryClient.setQueryData(coreKeys.me, me),
    [queryClient],
  );

  /** Nach Konto-Wechseln: alle Daten des alten Kontos verwerfen, dann /me setzen. */
  const resetFor = useCallback(
    (me: Me | null) => {
      // /me und die Konfiguration bleiben (beobachtete Queries), alles andere gehört zum alten Konto.
      const keep = new Set(['auth-config', 'me']);
      queryClient.removeQueries({
        predicate: (q) => q.queryKey[0] !== 'core' || !keep.has(String(q.queryKey[1])),
      });
      setMe(me);
      eventStream.restart(); // SSE gehört zum alten Konto
    },
    [queryClient, setMe],
  );

  const login = useCallback(
    async (email: string, password: string) => {
      clearPersonaOverride();
      if (supabase && config) {
        await supabaseSignInWithPassword(config, email, password);
        let me: Me | null;
        try {
          me = await fetchMeOrNull();
        } catch (error) {
          // Server lehnt die Anmeldung ab (z. B. fremde E-Mail-Domain, 403): Supabase-Sitzung nicht
          // stehen lassen, sonst hängt der Browser in einem Konto ohne Zugang.
          if (isApiError(error) && error.status === 403) await supabaseSignOut(config);
          throw error;
        }
        resetFor(me);
        return me;
      }
      const me = await coreApi.login(email, password);
      resetFor(me);
      return me;
    },
    [config, supabase, resetFor],
  );

  const loginPersona = useCallback(
    async (key: string) => {
      clearPersonaOverride();
      const me = await coreApi.loginPersona(key);
      resetFor(me);
      return me;
    },
    [resetFor],
  );

  const logout = useCallback(
    async (options: { all?: boolean } = {}) => {
      clearPersonaOverride();
      if (supabase && config) await supabaseSignOut(config);
      else await coreApi.logout(options.all === true);
      // Im Browser kann noch ein anderes Konto angemeldet sein (Kontowechsel).
      const remaining = supabase ? null : await fetchMeOrNull();
      resetFor(remaining);
      return remaining;
    },
    [config, supabase, resetFor],
  );

  const switchAccount = useCallback(
    async (accountId: string) => {
      clearPersonaOverride();
      const me = await coreApi.switchAccount(accountId);
      resetFor(me);
      return me;
    },
    [resetFor],
  );

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: coreKeys.me });
  }, [queryClient]);

  const status: AuthStatus =
    configQuery.isError || meQuery.isError
      ? 'error'
      : !config || meQuery.isPending
        ? 'loading'
        : meQuery.data
          ? 'authenticated'
          : 'anonymous';

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      me: meQuery.data ?? null,
      config,
      error: configQuery.error ?? meQuery.error,
      login,
      loginPersona,
      logout,
      switchAccount,
      refresh,
    }),
    [
      status,
      meQuery.data,
      config,
      configQuery.error,
      meQuery.error,
      login,
      loginPersona,
      logout,
      switchAccount,
      refresh,
    ],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth braucht einen <AuthProvider> (Plattform-Bootstrap).');
  return value;
}

/** Angemeldetes Konto oder null. */
export function useMe(): Me | null {
  return useAuth().me;
}
